import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { Trash2, FolderOpen, FilePlus, Folder, TerminalSquare, RefreshCw } from "lucide-react";

import TopBar from "./components/TopBar";
import EditorPane, { monacoLangForPath } from "./components/EditorPane";
import MobileEditorPane from "./components/MobileEditorPane";
import FileTabs from "./components/FileTabs";
import RightPanel from "./components/RightPanel";
import BottomTerminal from "./components/BottomTerminal";
import LiveTerminal from "./components/LiveTerminal";
import PreviewPanel from "./components/PreviewPanel";
import Toaster, { pushToast } from "./components/Toast";
import Drawer from "./components/Drawer";
import MobileBottomTabs from "./components/mobile/MobileBottomTabs";
import StdinBox from "./components/StdinBox";
import ThemeSelector from "./components/ThemeSelector";
import RecentProjectsList from "./components/RecentProjectsList";

import PromptModal from "./components/modals/PromptModal";
import ConfirmModal from "./components/modals/ConfirmModal";
import OpenProjectModal from "./components/modals/OpenProjectModal";
import ShareModal from "./components/modals/ShareModal";

import { fetchLanguages, runWeb } from "./lib/api";
import {
  createProject,
  deleteProject,
  getProject,
  listProjects,
  updateProject,
} from "./lib/projectApi";
import { createShare, getShare, buildShareUrl } from "./lib/shareApi";
import SAMPLES from "./data/samples";
import useRunStream from "./hooks/useRunStream";
import useBreakpoint from "./hooks/useBreakpoint";
import { readStoredTheme, writeStoredTheme } from "./lib/themes";
import { readRecent, pushRecent, removeRecent } from "./lib/recent";

// localStorage keys
const SCRATCH_KEY = (lang) => `runbox:scratch:${lang}`;
const LAST_LANG_KEY = "runbox:last-language";
const LAST_PROJECT_KEY = "runbox:last-project";

const PREFERRED_FALLBACK = "python";

function readScratch(lang) {
  try {
    const stored = localStorage.getItem(SCRATCH_KEY(lang));
    if (typeof stored === "string") return stored;
  } catch (_) { /* */ }
  return SAMPLES[lang] ?? "";
}
function writeScratch(lang, content) {
  try { localStorage.setItem(SCRATCH_KEY(lang), content); } catch (_) { /* */ }
}

export default function App() {
  // ---------- languages ----------
  const [languages, setLanguages] = useState([]);
  const [loadingLangs, setLoadingLangs] = useState(true);

  // ---------- single-file project state ----------
  const [language, setLanguage] = useState(PREFERRED_FALLBACK);
  const [code, setCode] = useState("");
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState("Scratchpad");
  const [savedSnap, setSavedSnap] = useState(null); // {name, language, code, files}
  const [saveState, setSaveState] = useState("idle");

  // ---------- multi-file state ----------
  const [files, setFiles] = useState([]);          // [{path, content}] — all project files
  const [activeFilePath, setActiveFilePath] = useState(null); // currently editing

  // ---------- right panel + preview ----------
  const [rightTab, setRightTab] = useState("output");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewStamp, setPreviewStamp] = useState(0);
  const [isRunningWeb, setIsRunningWeb] = useState(false);
  const [errorText, setErrorText] = useState("");

  // ---------- bottom terminal ----------
  const [termCollapsed, setTermCollapsed] = useState(false);
  const [termStatus, setTermStatus] = useState("idle");
  const termPanelRef = useRef(null);
  const terminalRef = useRef(null);         // desktop xterm
  const mobileTerminalRef = useRef(null);   // mobile xterm

  // ---------- modals ----------
  const [openModal, setOpenModal] = useState(null);
  const [openModalProps, setOpenModalProps] = useState({});

  function showModal(name, props = {}) {
    setOpenModal(name);
    setOpenModalProps(props);
  }
  function closeModal() {
    setOpenModal(null);
    setOpenModalProps({});
  }

  // ---------- editor theme (persisted) ----------
  const [themeId, setThemeId] = useState(() => readStoredTheme());
  useEffect(() => { writeStoredTheme(themeId); }, [themeId]);

  // ---------- pre-run stdin (transient, per-run) ----------
  const [stdinBuf, setStdinBuf] = useState("");

  // ---------- recent projects ----------
  const [recent, setRecent] = useState(() => readRecent());
  function refreshRecent() { setRecent(readRecent()); }

  // ---------- forked/shared snippet mode ----------
  const [forkedShareName, setForkedShareName] = useState(null);

  // ---------- responsive state ----------
  const bp = useBreakpoint();
  // Per spec: tablet treated the same as mobile.
  const isCompact = bp === "mobile" || bp === "tablet";
  const [mobileView, setMobileView] = useState("editor"); // files | editor | terminal | preview
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { if (!isCompact) setDrawerOpen(false); }, [isCompact]);

  // Mobile file list
  const [projectsList, setProjectsList] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  async function refreshProjectsList() {
    setProjectsLoading(true);
    try {
      const data = await listProjects(50, 0);
      setProjectsList(data.projects || []);
    } catch (e) {
      // non-fatal
    } finally {
      setProjectsLoading(false);
    }
  }

  // When switching to the mobile Files tab, refresh the list.
  useEffect(() => {
    if (isCompact && mobileView === "files") refreshProjectsList();
  }, [isCompact, mobileView]);

  // Write to whichever xterm instance is currently mounted.
  function termCall(method, ...args) {
    const a = terminalRef.current;
    const b = mobileTerminalRef.current;
    if (a && typeof a[method] === "function") a[method](...args);
    if (b && typeof b[method] === "function") b[method](...args);
  }

  // ---------- WS run stream ----------
  const stream = useRunStream({
    onStart: (msg) => {
      setTermStatus("connected");
      termCall("clear");
      termCall("writeInfo",
        `→ ${msg.language} · timeout ${msg.timeout}s${msg.has_compile ? " · compile+run" : ""}\r\n`
      );
    },
    onStage: (stage) => termCall("writeStageHeader", stage),
    onStdout: (data) => termCall("writeStdout", data),
    onStderr: (data) => termCall("writeStderr", data),
    onCompileError: (msg) => termCall("writeCompileFailed", msg.exit_code),
    onExit: (result) => {
      termCall("writeExitFooter", {
        exitCode: result.exit_code,
        execTimeMs: result.exec_time_ms,
        timedOut: result.timed_out,
        stopped: result.stopped,
      });
      setTermStatus("idle");
    },
    onError: (msg) => {
      setTermStatus("error");
      termCall("writeError", msg?.data || "unknown");
    },
    onClose: () => setTermStatus((s) => (s === "connected" ? "idle" : s)),
  });

  // ---------- bootstrap ----------
  useEffect(() => {
    let alive = true;
    // Read localStorage BEFORE any await — the projectId effect fires with null
    // after the first await yields, clearing LAST_PROJECT_KEY before we read it.
    const lastProjId = (() => {
      try { return localStorage.getItem(LAST_PROJECT_KEY); } catch (_) { return null; }
    })();
    // Detect shared snippet via ?s=<id>
    const shareId = (() => {
      try {
        const p = new URLSearchParams(window.location.search);
        return p.get("s");
      } catch (_) { return null; }
    })();
    (async () => {
      try {
        let langData = null;
        try {
          langData = await fetchLanguages();
        } catch (e) {
          // Graceful fallback so the picker is never empty
          // eslint-disable-next-line no-console
          console.warn("[noefforts] /api/languages failed, using fallback:", e);
          pushToast("Languages service unavailable — using minimal list", "warn", 4000);
          langData = {
            languages: [
              { id: "python", label: "Python", monaco_id: "python", file_ext: ".py", available: true, web: false, has_compile: false },
              { id: "javascript", label: "JavaScript", monaco_id: "javascript", file_ext: ".js", available: true, web: false, has_compile: false },
              { id: "html", label: "HTML", monaco_id: "html", file_ext: ".html", available: true, web: true, has_compile: false },
            ],
          };
        }
        if (!alive) return;
        setLanguages(langData?.languages || []);

        // If a shared snippet is requested, load it as a forked scratch
        if (shareId) {
          try {
            const snip = await getShare(shareId);
            if (!alive) return;
            applyForkedShare(snip);
            // Strip ?s= from URL so refresh doesn't re-fetch
            try {
              const u = new URL(window.location.href);
              u.searchParams.delete("s");
              window.history.replaceState({}, "", u.toString());
            } catch (_) { /* */ }
            pushToast(`Opened shared snippet${snip.name ? ': ' + snip.name : ''}`, "success", 2500);
            setLoadingLangs(false);
            return;
          } catch (e) {
            pushToast(`Shared link invalid: ${e.message}`, "error", 4000);
          }
        }

        // Try to restore last project (lastProjId was read before the first await)
        if (lastProjId) {
          try {
            const p = await getProject(lastProjId);
            if (!alive) return;
            applyLoadedProject(p);
            setLoadingLangs(false);
            return;
          } catch (e) {
            try { localStorage.removeItem(LAST_PROJECT_KEY); } catch (_) { /* */ }
          }
        }
        // Scratchpad fallback with last-used language
        const lastLang = (() => {
          try { return localStorage.getItem(LAST_LANG_KEY) || PREFERRED_FALLBACK; }
          catch (_) { return PREFERRED_FALLBACK; }
        })();
        const langInfo = (langData?.languages || []).find((l) => l.id === lastLang) ||
                         (langData?.languages || []).find((l) => l.available);
        if (langInfo) enterScratchpadFor(langInfo.id);
      } catch (e) {
        pushToast(`Startup failed: ${e.message}`, "error", 5000);
      } finally {
        if (alive) setLoadingLangs(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist last project + last language
  useEffect(() => {
    try {
      if (projectId) localStorage.setItem(LAST_PROJECT_KEY, projectId);
      else localStorage.removeItem(LAST_PROJECT_KEY);
    } catch (_) { /* */ }
  }, [projectId]);
  useEffect(() => {
    try { localStorage.setItem(LAST_LANG_KEY, language); } catch (_) { /* */ }
  }, [language]);

  // ---------- project helpers ----------
  function enterScratchpadFor(nextLang) {
    setProjectId(null);
    setProjectName("Scratchpad");
    setSavedSnap(null);
    setSaveState("idle");
    setLanguage(nextLang);
    setCode(readScratch(nextLang));
    setFiles([]);
    setActiveFilePath(null);
    setPreviewUrl(null);
    setPreviewStamp(0);
    setErrorText("");
    setRightTab(nextLang === "html" ? "preview" : "output");
  }

  function applyLoadedProject(p) {
    setProjectId(p.id);
    setProjectName(p.name);
    setLanguage(p.language);
    setForkedShareName(null);
    // Multi-file support: load files from project
    const projectFiles = p.files && p.files.length > 0
      ? p.files.map((f) => ({ path: f.path, content: f.content || "" }))
      : [{ path: `main${_langExt(p.language)}`, content: p.code || "" }];
    const entryPath = p.entry_file || projectFiles[0]?.path;
    const activeCode = projectFiles.find((f) => f.path === entryPath)?.content || p.code || "";
    setFiles(projectFiles);
    setActiveFilePath(entryPath || null);
    setCode(activeCode);
    setSavedSnap({ name: p.name, language: p.language, code: activeCode, files: projectFiles });
    setSaveState("saved");
    setPreviewUrl(null);
    setPreviewStamp(0);
    setErrorText("");
    setRightTab(p.language === "html" ? "preview" : "output");
    // Track in recent projects list
    pushRecent(p);
    refreshRecent();
  }

  // Apply a shared snippet as a scratchpad fork (read from ?s= link).
  // Starts as a Scratchpad — user can then Save As to persist.
  function applyForkedShare(snip) {
    setProjectId(null);
    setProjectName("Scratchpad");
    setSavedSnap(null);
    setSaveState("idle");
    setLanguage(snip.language);
    setForkedShareName(snip.name || null);
    if (snip.files && snip.files.length > 0) {
      const projectFiles = snip.files.map((f) => ({ path: f.path, content: f.content || "" }));
      const entryPath = snip.entry_file || projectFiles[0]?.path;
      const activeCode = projectFiles.find((f) => f.path === entryPath)?.content || "";
      setFiles(projectFiles);
      setActiveFilePath(entryPath || null);
      setCode(activeCode);
    } else {
      setFiles([]);
      setActiveFilePath(null);
      setCode(snip.code || "");
    }
    setPreviewUrl(null);
    setPreviewStamp(0);
    setErrorText("");
    setRightTab(snip.language === "html" ? "preview" : "output");
  }

  // ---------- language switcher ----------
  const handleLanguageChange = useCallback((nextId) => {
    const langInfo = languages.find((l) => l.id === nextId);
    if (!langInfo) return;

    if (projectId) {
      // Project mode: update language; marks project dirty
      setLanguage(nextId);
      pushToast(`Project language: ${langInfo.label}`, "info", 2000);
    } else {
      // Scratchpad: save current scratch, load target language's scratch
      writeScratch(language, code);
      enterScratchpadFor(nextId);
    }
    if (nextId === "html") setRightTab("preview");
    else if (rightTab === "preview") setRightTab("output");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages, projectId, language, code, rightTab]);

  // ---------- code change ----------
  const handleCodeChange = useCallback((v) => {
    setCode(v);
    if (projectId && activeFilePath) {
      // Sync to the active file in the multi-file array
      setFiles((prev) => prev.map((f) => f.path === activeFilePath ? { ...f, content: v } : f));
    } else if (!projectId) {
      writeScratch(language, v);
    }
  }, [projectId, language, activeFilePath]);

  // ---------- run ----------
  const handleRun = useCallback(async () => {
    const langInfo = languages.find((l) => l.id === language);
    if (!langInfo) {
      setErrorText(`Unknown language: ${language}`);
      return;
    }
    if (!langInfo.web && !langInfo.available) {
      setErrorText(`Runtime not installed for ${langInfo.label}`);
      pushToast(`${langInfo.label} runtime is not installed`, "error", 4500);
      return;
    }
    setErrorText("");

    if (langInfo.web) {
      setIsRunningWeb(true);
      setRightTab("preview");
      if (isCompact) setMobileView("preview");
      try {
        // Single-file HTML goes via `html` field; server serves it as index.html
        const resp = await runWeb({ html: code });
        setPreviewUrl(resp.preview_url);
        setPreviewStamp(Date.now());
        pushToast("Preview rendered", "success", 2200);
      } catch (e) {
        pushToast(`Preview failed: ${e.message}`, "error", 5000);
        setErrorText(`Preview failed: ${e.message}`);
      } finally {
        setIsRunningWeb(false);
      }
      return;
    }

    // WS-streamed run
    if (termCollapsed) {
      setTermCollapsed(false);
      try { termPanelRef.current?.expand(); } catch (_) { /* */ }
    }
    setRightTab("output");
    if (isCompact) setMobileView("terminal");
    // Multi-file: pass all files when there are multiple
    const runFiles = projectId && files.length > 1
      ? files.map((f) => ({ path: f.path, content: f.path === activeFilePath ? code : f.content }))
      : null;
    // Pre-run stdin: ensure trailing newline if user didn't include one
    const preStdin = stdinBuf && !stdinBuf.endsWith("\n") ? stdinBuf + "\n" : stdinBuf;
    stream.run({ language, code, files: runFiles, entry_file: activeFilePath, timeout: 30, stdin: preStdin || undefined });
    // Auto-focus the terminal so user can type immediately
    setTimeout(() => termCall("focus"), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages, language, code, termCollapsed, isCompact, stream, projectId, files, activeFilePath, stdinBuf]);

  const handleStop = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log("[noefforts] Stop clicked");
    const sent = stream.stop();
    if (sent) pushToast("Stop requested", "info", 1500);
    else pushToast("Nothing to stop", "info", 2000);
  }, [stream]);

  function toggleTerminal() {
    setTermCollapsed((cur) => {
      const next = !cur;
      try {
        if (next) termPanelRef.current?.collapse();
        else termPanelRef.current?.expand();
      } catch (_) { /* */ }
      return next;
    });
  }

  // ---------- project actions ----------
  async function doSave() {
    if (!projectId) {
      showModal("saveAs", { defaultValue: projectName === "Scratchpad" ? "" : projectName });
      return;
    }
    try {
      setSaveState("saving");
      // Build latest files list with current code merged in
      const latestFiles = files.length > 0
        ? files.map((f) => f.path === activeFilePath ? { ...f, content: code } : f)
        : null;
      const updated = await updateProject(projectId, {
        name: projectName,
        language,
        code,
        files: latestFiles && latestFiles.length > 1 ? latestFiles : null,
        entry_file: activeFilePath || null,
      });
      const savedFiles = updated.files?.length > 0
        ? updated.files.map((f) => ({ path: f.path, content: f.content || "" }))
        : latestFiles || [];
      setFiles(savedFiles);
      setSavedSnap({ name: updated.name, language: updated.language, code: updated.code || "", files: savedFiles });
      setSaveState("saved");
      pushToast("Project saved", "success", 1500);
    } catch (e) {
      setSaveState("error");
      pushToast(`Save failed: ${e.message}`, "error");
    }
  }

  async function doSaveAs(name) {
    setSaveState("saving");
    try {
      const latestFiles = files.length > 1
        ? files.map((f) => f.path === activeFilePath ? { ...f, content: code } : f)
        : null;
      const created = await createProject({
        name,
        language,
        code,
        files: latestFiles,
        entry_file: activeFilePath || null,
      });
      const savedFiles = created.files?.length > 0
        ? created.files.map((f) => ({ path: f.path, content: f.content || "" }))
        : [{ path: activeFilePath || `main${_langExt(language)}`, content: code }];
      setProjectId(created.id);
      setProjectName(created.name);
      setFiles(savedFiles);
      setActiveFilePath(created.entry_file || savedFiles[0]?.path);
      setSavedSnap({ name: created.name, language: created.language, code: created.code || "", files: savedFiles });
      setSaveState("saved");
      pushToast(`Saved as "${created.name}"`, "success", 2000);
      // refresh mobile list if open
      if (isCompact && mobileView === "files") refreshProjectsList();
    } catch (e) {
      setSaveState("error");
      throw e; // let PromptModal show error
    }
  }

  async function doOpen(id) {
    try {
      const p = await getProject(id);
      applyLoadedProject(p);
      pushToast(`Opened "${p.name}"`, "success", 1500);
      if (isCompact) setMobileView("editor");
    } catch (e) {
      pushToast(`Open failed: ${e.message}`, "error", 4000);
    }
  }

  async function doDelete() {
    if (!projectId) return;
    const victim = projectId;
    try {
      await deleteProject(victim);
      pushToast("Project deleted", "info", 1800);
      setProjectId(null);
      setProjectName("Scratchpad");
      setSavedSnap(null);
      setSaveState("idle");
      // Drop into scratchpad for the current language
      enterScratchpadFor(language);
      if (isCompact && mobileView === "files") refreshProjectsList();
    } catch (e) {
      pushToast(`Delete failed: ${e.message}`, "error");
    }
  }

  async function doDeleteById(id, name) {
    try {
      await deleteProject(id);
      pushToast(`Deleted "${name}"`, "info", 1800);
      if (id === projectId) {
        setProjectId(null);
        setProjectName("Scratchpad");
        setSavedSnap(null);
        setSaveState("idle");
        enterScratchpadFor(language);
      }
      refreshProjectsList();
    } catch (e) {
      pushToast(`Delete failed: ${e.message}`, "error");
    }
  }

  function handleProjectNew() {
    showModal("newProject", {});
  }

  // ---------- auto-save (every 30s if project is dirty) ----------
  useEffect(() => {
    if (!projectId) return;
    const interval = setInterval(() => {
      if (!isDirtyRef.current) return;
      if (stream.isRunning) return;
      silentSave();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Track dirty state via ref so the interval above is stable
  const isDirtyRef = useRef(false);

  async function silentSave() {
    if (!projectId) return;
    try {
      const latestFiles = files.length > 0
        ? files.map((f) => f.path === activeFilePath ? { ...f, content: code } : f)
        : null;
      const updated = await updateProject(projectId, {
        name: projectName,
        language,
        code,
        files: latestFiles && latestFiles.length > 1 ? latestFiles : null,
        entry_file: activeFilePath || null,
      });
      const savedFiles = updated.files?.length > 0
        ? updated.files.map((f) => ({ path: f.path, content: f.content || "" }))
        : latestFiles || [];
      setFiles(savedFiles);
      setSavedSnap({ name: updated.name, language: updated.language, code: updated.code || "", files: savedFiles });
      setSaveState("saved");
      pushToast("Auto-saved", "info", 1200);
    } catch (e) {
      // silent
    }
  }

  // ---------- share ----------
  const handleShare = useCallback(() => {
    showModal("share");
  }, []);

  async function doCreateShare() {
    const latestFiles = files.length > 1
      ? files.map((f) => f.path === activeFilePath ? { ...f, content: code } : f)
      : null;
    const snip = await createShare({
      language,
      code,
      files: latestFiles,
      entry_file: activeFilePath || null,
      name: projectId ? projectName : (forkedShareName || ""),
    });
    return { url: buildShareUrl(snip.id) };
  }

  // ---------- output download ----------
  function handleDownloadOutput() {
    const result = stream.lastResult;
    const lang = language;
    const name = (projectId ? projectName : "output").replace(/[^\w.-]+/g, "_");
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const lines = [];
    lines.push(`# ${name} — ${lang} — ${ts}`);
    if (result) {
      lines.push(`# exit ${result.exit_code} · ${result.exec_time_ms}ms` +
        (result.peak_memory_kb ? ` · peak ${(result.peak_memory_kb/1024).toFixed(1)}MB` : "") +
        (result.timed_out ? " · timed out" : "") +
        (result.stopped ? " · stopped" : ""));
    }
    lines.push("");
    lines.push("=== STDOUT ===");
    lines.push(result?.stdout || "");
    if (result?.stderr) {
      lines.push("");
      lines.push("=== STDERR ===");
      lines.push(result.stderr);
    }
    if (result?.compile_error) {
      lines.push("");
      lines.push("=== COMPILE ERROR ===");
      lines.push(result.compile_error);
    }
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${name}-${ts}.txt`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 0);
    pushToast("Output downloaded", "success", 1500);
  }

  // ---------- swipe gestures (mobile: editor ↔ terminal/preview) ----------
  const swipeRef = useRef({ x: 0, y: 0, active: false });
  function onTouchStart(e) {
    if (!isCompact) return;
    const t = e.touches[0];
    if (!t) return;
    swipeRef.current = { x: t.clientX, y: t.clientY, active: true };
  }
  function onTouchEnd(e) {
    const s = swipeRef.current;
    if (!s.active) return;
    swipeRef.current = { x: 0, y: 0, active: false };
    const t = (e.changedTouches && e.changedTouches[0]) || null;
    if (!t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    // Only horizontal swipes with strong x-dominance
    if (Math.abs(dx) < 80 || Math.abs(dx) < Math.abs(dy) * 1.8) return;
    const order = isHtml
      ? ["files", "editor", "preview", "terminal"]
      : ["files", "editor", "terminal"];
    const idx = order.indexOf(mobileView);
    if (idx === -1) return;
    const next = dx < 0 ? order[Math.min(order.length - 1, idx + 1)] : order[Math.max(0, idx - 1)];
    if (next !== mobileView) setMobileView(next);
  }

  // ---------- multi-file helpers ----------
  // Helper: get file extension for language (uses loaded languages, fallback to .txt)
  function _langExt(lang) {
    return languages.find((l) => l.id === lang)?.file_ext || ".txt";
  }

  function switchFile(path) {
    if (path === activeFilePath) return;
    // Save current code into the files array
    setFiles((prev) => prev.map((f) => f.path === activeFilePath ? { ...f, content: code } : f));
    // Load the target file
    const target = files.find((f) => f.path === path);
    if (target) { setActiveFilePath(path); setCode(target.content); }
    setSaveState((s) => s === "saved" ? "idle" : s);
  }

  function handleAddFile(newPath) {
    if (files.some((f) => f.path === newPath)) {
      pushToast("File already exists", "warn", 2000);
      return;
    }
    setFiles((prev) => {
      const updated = prev.map((f) => f.path === activeFilePath ? { ...f, content: code } : f);
      return [...updated, { path: newPath, content: "" }];
    });
    setActiveFilePath(newPath);
    setCode("");
    setSaveState("idle");
  }

  function handleDeleteFile(path) {
    if (files.length <= 1) { pushToast("Can't delete the only file", "warn", 2500); return; }
    const remaining = files.filter((f) => f.path !== path);
    setFiles(remaining);
    if (activeFilePath === path) {
      setActiveFilePath(remaining[0].path);
      setCode(remaining[0].content);
    }
    setSaveState("idle");
  }

  // ---------- shortcuts ----------
  useEffect(() => {
    function onKey(e) {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key === "Enter") {
        e.preventDefault();
        if (!stream.isRunning && !isRunningWeb) handleRun();
      } else if (meta && e.key === ".") {
        e.preventDefault();
        if (stream.isRunning) handleStop();
      } else if (meta && (e.key === "`" || e.code === "Backquote")) {
        e.preventDefault();
        toggleTerminal();
      } else if (meta && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        doSave();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleRun, handleStop, stream.isRunning, isRunningWeb, projectId, projectName, language, code]);

  // ---------- derived ----------
  const langInfo = useMemo(
    () => languages.find((l) => l.id === language),
    [languages, language]
  );
  const isHtml = !!langInfo?.web;
  const topStatus = isHtml
    ? isRunningWeb ? "running" : previewUrl ? "success" : errorText ? "error" : "idle"
    : stream.status;
  const isRunningAny = stream.isRunning || isRunningWeb;

  const isDirty = useMemo(() => {
    if (!projectId || !savedSnap) return false;
    if (savedSnap.code !== code || savedSnap.language !== language || savedSnap.name !== projectName) return true;
    // Multi-file: check if any file differs from saved
    if (savedSnap.files && files.length > 0) {
      const latestFiles = files.map((f) => f.path === activeFilePath ? { ...f, content: code } : f);
      if (latestFiles.length !== savedSnap.files.length) return true;
      for (const lf of latestFiles) {
        const sf = savedSnap.files.find((f) => f.path === lf.path);
        if (!sf || sf.content !== lf.content) return true;
      }
    }
    return false;
  }, [projectId, savedSnap, code, language, projectName, files, activeFilePath]);

  // Sync dirty state to ref for the auto-save interval
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);

  const syntheticPath = useMemo(() => {
    // Multi-file: use actual file path for Monaco language detection
    if (activeFilePath) return activeFilePath;
    const ext = langInfo?.file_ext || ".txt";
    return `main${ext}`;
  }, [langInfo, activeFilePath]);
  const monacoLanguage = monacoLangForPath(syntheticPath, langInfo?.monaco_id);

  // ---------- top bar common props ----------
  const topBarProps = {
    languages,
    language,
    onLanguageChange: handleLanguageChange,
    loading: loadingLangs,
    isRunning: isRunningAny,
    onRun: handleRun,
    onStop: handleStop,
    status: topStatus,
    execTimeMs: stream.lastResult?.exec_time_ms,
    exitCode: stream.lastResult?.exec_time_ms != null ? stream.lastResult?.exit_code : undefined,
    projectName: projectId ? projectName : (forkedShareName ? `Forked: ${forkedShareName}` : "Scratchpad"),
    isProjectMode: !!projectId,
    saveState,
    isDirty,
    onProjectNew: handleProjectNew,
    onProjectOpen: () => showModal("openProject"),
    onProjectSave: doSave,
    onProjectSaveAs: () => showModal("saveAs", { defaultValue: projectName === "Scratchpad" ? "" : projectName }),
    onProjectDelete: () => showModal("confirmDeleteProject", {
      message: `Delete project "${projectName}"? This cannot be undone.`,
    }),
    onShare: handleShare,
    themeId,
    onThemeChange: setThemeId,
    compact: isCompact,
    onMobileMenu: () => setDrawerOpen(true),
  };

  // ---------- editor block (device-aware) ----------
  // FileTabs: show only in project mode with at least 1 file
  // Compute per-file dirty state by comparing with savedSnap
  const fileTabsWithDirty = useMemo(() => {
    if (!projectId || files.length === 0) return [];
    return files.map((f) => {
      const savedFile = savedSnap?.files?.find((sf) => sf.path === f.path);
      const currentContent = f.path === activeFilePath ? code : f.content;
      const dirty = !savedFile || savedFile.content !== currentContent;
      return { ...f, content: currentContent, dirty };
    });
  }, [projectId, files, savedSnap, activeFilePath, code]);

  const FileTabsBlock = projectId && fileTabsWithDirty.length > 0 ? (
    <FileTabs
      files={fileTabsWithDirty}
      activeFile={activeFilePath}
      onSwitch={switchFile}
      onAdd={handleAddFile}
      onDelete={handleDeleteFile}
      language={language}
      langInfo={langInfo}
    />
  ) : null;

  const EditorBlock = isCompact ? (
    <MobileEditorPane
      path={syntheticPath}
      monacoLanguage={monacoLanguage}
      value={code}
      onChange={handleCodeChange}
      onRequestRun={handleRun}
      isTablet={bp === "tablet"}
      themeId={themeId}
    />
  ) : (
    <EditorPane
      path={syntheticPath}
      monacoLanguage={monacoLanguage}
      value={code}
      onChange={handleCodeChange}
      onRequestRun={handleRun}
      themeId={themeId}
    />
  );

  // Combined editor section: tabs on top + device editor below + stdin input
  const EditorSection = (
    <div className="h-full flex flex-col bg-ide-panel2">
      {FileTabsBlock}
      <div className="flex-1 min-h-0 overflow-hidden">
        {EditorBlock}
      </div>
      <StdinBox value={stdinBuf} onChange={setStdinBuf} disabled={stream.isRunning} />
    </div>
  );

  // ---------- mobile file-list view (saved projects) ----------
  const MobileFilesView = (
    <div className="h-full flex flex-col bg-ide-panel2">
      <div className="flex items-center px-3 py-2 border-b border-ide-border">
        <span className="text-[11px] uppercase tracking-wider text-ide-mute">Projects</span>
        <button
          onClick={refreshProjectsList}
          className="ml-auto text-ide-mute hover:text-ide-text rounded p-1.5 hover:bg-ide-surface min-h-[36px] min-w-[36px] flex items-center justify-center"
          title="Refresh"
        >
          <RefreshCw className={"h-4 w-4 " + (projectsLoading ? "animate-spin" : "")} />
        </button>
      </div>
      <div className="p-3 flex gap-2">
        <button
          onClick={handleProjectNew}
          className="flex items-center gap-1.5 px-3 py-2 min-h-[36px] rounded-md border border-ide-border bg-ide-panel hover:bg-ide-surface text-sm text-ide-text"
        >
          <FilePlus className="h-3.5 w-3.5" /> New
        </button>
        <button
          onClick={() => showModal("saveAs", { defaultValue: projectName === "Scratchpad" ? "" : projectName })}
          className="flex items-center gap-1.5 px-3 py-2 min-h-[36px] rounded-md border border-ide-border bg-ide-panel hover:bg-ide-surface text-sm text-ide-text"
        >
          <FolderOpen className="h-3.5 w-3.5" /> Save As
        </button>
      </div>
      <div className="flex-1 overflow-auto pb-3">
        {recent.length > 0 && (
          <RecentProjectsList
            items={recent}
            onOpen={(id) => doOpen(id)}
            onRemove={(id) => { removeRecent(id); refreshRecent(); }}
            currentId={projectId}
          />
        )}
        <div className="px-2 pt-2">
          <div className="px-1 py-1 text-[10px] uppercase tracking-wider text-ide-mute">All projects</div>
          {projectsList.length === 0 && !projectsLoading && (
            <div className="text-center text-ide-mute text-sm py-8 px-4">
              No saved projects yet. Tap <span className="text-ide-text">Save As</span> above to create one.
            </div>
          )}
          {projectsList.map((p) => (
            <div
              key={p.id}
              className={
                "flex items-center gap-2 px-2 py-2 my-1 rounded-md transition-colors " +
                (p.id === projectId ? "bg-ide-surface" : "hover:bg-ide-panel")
              }
            >
              <button
                onClick={() => doOpen(p.id)}
                className="flex-1 text-left min-w-0 min-h-[36px] flex flex-col justify-center"
              >
                <div className="flex items-center gap-2">
                  <Folder className="h-3.5 w-3.5 text-purple-300 flex-none" />
                  <span className="truncate text-ide-text text-sm">{p.name}</span>
                  {p.id === projectId && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">open</span>
                  )}
                </div>
                <div className="text-[11px] text-ide-mute font-mono truncate mt-0.5">
                  {p.language} · {new Date(p.updated_at).toLocaleString()}
                </div>
              </button>
              <button
                onClick={() => showModal("confirmDeleteProjectById", {
                  id: p.id, name: p.name,
                  message: `Delete project "${p.name}"? This cannot be undone.`,
                })}
                className="text-ide-mute hover:text-rose-300 rounded p-1.5 hover:bg-rose-500/10 min-h-[36px] min-w-[36px] flex items-center justify-center"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="w-full flex flex-col bg-ide-bg text-ide-text overflow-hidden"
      style={{
        // Lock the shell to the visible viewport; `100dvh` tracks the iOS
        // URL-bar collapse/expand so the top/bottom bars never get pushed
        // off-screen. Fallback to `100vh` for older browsers.
        height: "100vh",
        // Modern browsers: dvh wins because it appears after vh.
        maxHeight: "100dvh",
        minHeight: "100dvh",
      }}
    >
      <TopBar {...topBarProps} />

      {/* ===================== DESKTOP ===================== */}
      {!isCompact && (
        <div className="flex-1 min-h-0">
          <PanelGroup direction="horizontal" autoSaveId="runbox-h-simple">
            <Panel defaultSize={68} minSize={28}>
              <PanelGroup direction="vertical" autoSaveId="runbox-v-simple">
                <Panel defaultSize={70} minSize={20}>
                  {EditorSection}
                </Panel>
                <PanelResizeHandle className="h-px bg-ide-border hover:bg-ide-borderHi transition-colors" />
                <Panel
                  ref={termPanelRef}
                  defaultSize={30}
                  minSize={6}
                  collapsible
                  collapsedSize={5}
                  onCollapse={() => setTermCollapsed(true)}
                  onExpand={() => setTermCollapsed(false)}
                >
                  <BottomTerminal
                    collapsed={termCollapsed}
                    onToggle={toggleTerminal}
                    status={termStatus}
                    onClear={() => terminalRef.current?.clear()}
                    terminalRef={terminalRef}
                    onKeyInput={stream.isRunning ? (d) => stream.sendStdin(d) : null}
                    themeId={themeId}
                    onDownload={handleDownloadOutput}
                    execStats={stream.lastResult}
                  />
                </Panel>
              </PanelGroup>
            </Panel>
            <PanelResizeHandle className="w-px bg-ide-border hover:bg-ide-borderHi transition-colors" />
            <Panel defaultSize={32} minSize={18}>
              <RightPanel
                tab={rightTab}
                onTabChange={setRightTab}
                result={stream.lastResult}
                isRunning={stream.isRunning}
                errorText={errorText}
                language={language}
                previewUrl={previewUrl}
                runStamp={previewStamp}
              />
            </Panel>
          </PanelGroup>
        </div>
      )}

      {/* ===================== MOBILE / TABLET ===================== */}
      {isCompact && (
        <>
          <div
            className="flex-1 min-h-0 relative"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {/* All four panels are absolutely positioned at full size so
                Monaco / xterm / iframe always see real dimensions. Inactive
                panels are made inert via visibility/pointer-events/opacity
                instead of `display:none` \u2014 otherwise Monaco's
                automaticLayout crashes with "Cannot read properties of
                undefined (reading 'dimensions')". */}
            {[
              { id: "files",    node: MobileFilesView },
              { id: "editor",   node: EditorSection },
              { id: "terminal", node: (
                <div className="h-full flex flex-col bg-ide-panel2">
                  <div className="flex items-center px-3 py-1.5 border-b border-ide-border text-[11px] uppercase tracking-wider text-ide-mute">
                    <TerminalSquare className="h-3.5 w-3.5 mr-1.5" />
                    terminal
                    <span
                      className={
                        "ml-2 inline-block h-1.5 w-1.5 rounded-full " +
                        (termStatus === "connected" ? "bg-emerald-400 animate-pulse-dot" :
                         termStatus === "error" ? "bg-rose-400" : "bg-ide-mute")
                      }
                    />
                    <button
                      className="ml-auto text-ide-mute hover:text-ide-text rounded p-1 hover:bg-ide-surface min-h-[36px] min-w-[36px] flex items-center justify-center"
                      onClick={() => mobileTerminalRef.current?.clear()}
                      title="Clear"
                    >
                      clear
                    </button>
                  </div>
                  <div className="flex-1 min-h-0">
                    <LiveTerminal ref={mobileTerminalRef} onKeyInput={stream.isRunning ? (d) => stream.sendStdin(d) : null} themeId={themeId} />
                  </div>
                </div>
              ) },
              { id: "preview",  node: (
                <PreviewPanel
                  enabled={isHtml}
                  previewUrl={previewUrl}
                  runStamp={previewStamp}
                />
              ) },
            ].map(({ id, node }) => {
              const active = mobileView === id;
              return (
                <div
                  key={id}
                  className="absolute inset-0 flex flex-col bg-ide-panel2 transition-opacity"
                  style={{
                    visibility: active ? "visible" : "hidden",
                    pointerEvents: active ? "auto" : "none",
                    opacity: active ? 1 : 0,
                    zIndex: active ? 2 : 1,
                  }}
                  aria-hidden={!active}
                >
                  {node}
                </div>
              );
            })}
          </div>
          <MobileBottomTabs
            active={mobileView}
            onChange={setMobileView}
            previewEnabled={isHtml}
          />
          <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Project">
            <div className="p-3 flex flex-col gap-1">
              <DrawerButton
                onClick={() => { handleProjectNew(); setDrawerOpen(false); }}
                icon={<FilePlus className="h-4 w-4" />}
              >New project</DrawerButton>
              <DrawerButton
                onClick={() => { showModal("openProject"); setDrawerOpen(false); }}
                icon={<FolderOpen className="h-4 w-4" />}
              >Open…</DrawerButton>
              <div className="h-px bg-ide-border my-1" />
              <DrawerButton
                onClick={() => { doSave(); setDrawerOpen(false); }}
                icon={<FilePlus className="h-4 w-4" />}
                kbd="⌘/Ctrl+S"
              >Save</DrawerButton>
              <DrawerButton
                onClick={() => {
                  showModal("saveAs", { defaultValue: projectName === "Scratchpad" ? "" : projectName });
                  setDrawerOpen(false);
                }}
                icon={<FilePlus className="h-4 w-4" />}
              >Save As…</DrawerButton>
              <div className="h-px bg-ide-border my-1" />
              <div className="px-2 py-1.5 flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-wider text-ide-mute">Theme</span>
                <div className="ml-auto">
                  <ThemeSelector themeId={themeId} onChange={setThemeId} compact />
                </div>
              </div>
              <div className="h-px bg-ide-border my-1" />
              <DrawerButton
                onClick={() => {
                  if (!projectId) return;
                  showModal("confirmDeleteProject", {
                    message: `Delete project "${projectName}"? This cannot be undone.`,
                  });
                  setDrawerOpen(false);
                }}
                disabled={!projectId}
                icon={<Trash2 className="h-4 w-4" />}
                danger
              >Delete…</DrawerButton>
              <div className="h-px bg-ide-border my-1" />
              <div className="px-2 pt-2 text-[11px] text-ide-mute font-mono">
                <div className="truncate">{projectId ? projectName : "Scratchpad"}</div>
                <div>{language} · {saveState}{isDirty ? " · unsaved" : ""}</div>
              </div>
            </div>
          </Drawer>
        </>
      )}

      {/* ===================== MODALS ===================== */}
      <PromptModal
        open={openModal === "saveAs"}
        title="Save project as"
        label="project name"
        defaultValue={openModalProps.defaultValue || ""}
        placeholder="My Project"
        helper="1–80 characters."
        validate={(v) => {
          if (v.length > 80) return "name too long";
          if (!v.trim()) return "name required";
          return null;
        }}
        confirmLabel="Save"
        onClose={closeModal}
        onConfirm={async (v) => { await doSaveAs(v.trim()); }}
      />

      <PromptModal
        open={openModal === "newProject"}
        title="New project"
        label="project name"
        defaultValue=""
        placeholder="My Project"
        validate={(v) => {
          if (v.length > 80) return "name too long";
          if (!v.trim()) return "name required";
          return null;
        }}
        confirmLabel="Create"
        onClose={closeModal}
        onConfirm={async (v) => {
          // Reset editor to an empty scratchpad starter in current language, then save as
          const starter = SAMPLES[language] || "";
          setCode(starter);
          await doSaveAs(v.trim());
        }}
      />

      <OpenProjectModal
        open={openModal === "openProject"}
        onClose={closeModal}
        onPick={(id) => doOpen(id)}
      />

      <ConfirmModal
        open={openModal === "confirmDeleteProject"}
        title="Delete project"
        message={openModalProps.message || ""}
        confirmLabel="Delete"
        onClose={closeModal}
        onConfirm={doDelete}
      />

      <ConfirmModal
        open={openModal === "confirmDeleteProjectById"}
        title="Delete project"
        message={openModalProps.message || ""}
        confirmLabel="Delete"
        onClose={closeModal}
        onConfirm={() => doDeleteById(openModalProps.id, openModalProps.name)}
      />

      <ShareModal
        open={openModal === "share"}
        onClose={closeModal}
        onCreate={doCreateShare}
      />

      <Toaster />
    </div>
  );
}

function DrawerButton({ onClick, disabled, icon, danger, kbd, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={
        "flex items-center gap-2 w-full px-3 py-2 rounded-md text-left text-sm transition-colors min-h-[44px] disabled:opacity-40 disabled:cursor-not-allowed " +
        (danger ? "text-rose-300 hover:bg-rose-500/10" : "text-ide-text hover:bg-ide-surface")
      }
    >
      {icon}
      <span className="flex-1">{children}</span>
      {kbd && (
        <span className="text-[10px] text-ide-mute font-mono px-1 py-0.5 rounded border border-ide-border">
          {kbd}
        </span>
      )}
    </button>
  );
}
